import { MapPin, Calendar, Clock, Users } from "lucide-mui"
import { DateTime } from "luxon"
import React from "react"

import i18n from "@/i18n"

type VenueDetailsCardProps = {
  venueName: string
  venueLogoUrl?: string | null
  address?: string | null
  city?: string | null
  latitude?: number | null
  longitude?: number | null
  date?: DateTime | null
  startTime?: string | null
  endTime?: string | null
  peopleAmount?: number
  tableName?: string
  tableGroupName?: string
}

const VenueDetailsCard: React.FC<VenueDetailsCardProps> = ({
  venueName,
  venueLogoUrl,
  address,
  city,
  latitude,
  longitude,
  date,
  startTime,
  endTime,
  peopleAmount,
  tableName,
  tableGroupName,
}) => {
  const getDirectionsUrl = () => {
    if (latitude != null && longitude != null) {
      return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`
    }
    const query = `${address}, ${city}`.trim()
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
  }

  return (
    <div className="bg-gray-100 rounded-lg p-6" data-testid="public-venue-details-card-container">
      <div className="flex items-start gap-4 mb-4">
        {!!venueLogoUrl && (
          <img
            src={venueLogoUrl}
            alt={venueName}
            className="w-16 h-16 rounded object-cover flex-shrink-0"
            data-testid="public-venue-details-card-logo"
          />
        )}
        <div className="flex-1">
          <h3 className="font-bold text-lg mb-1 mt-0 " data-testid="public-venue-details-card-name">
            {venueName}
          </h3>
          {!!(address || city) && (
            <div
              className="flex items-start gap-2 text-sm text-gray-600"
              data-testid="public-venue-details-card-address"
            >
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <div>
                  {address}
                  {address && city && ", "}
                  {city}
                </div>
                <a
                  href={getDirectionsUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                  data-testid="public-venue-details-card-directions-link"
                >
                  {i18n.t("app.public.venue.get_directions")}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {!!(date || startTime || peopleAmount || tableName) && (
        <>
          <div className="border-t border-gray-300 my-8" />
          <div>
            <h4
              className="font-bold mb-3 border-b border-solid border-0 border-gray-300 pb-2"
              data-testid="public-venue-details-card-summary-title"
            >
              {i18n.t("app.public.venue.booking_summary")}
            </h4>
            <div className="space-y-2 text-sm">
              {!!date && (
                <div className="flex items-center justify-between" data-testid="public-venue-details-card-date">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>{i18n.t("app.public.venue.date")}</span>
                  </div>
                  <span className="font-medium">{date.toFormat("dd MMM yyyy")}</span>
                </div>
              )}

              {!!startTime && !!endTime && (
                <div className="flex items-center justify-between" data-testid="public-venue-details-card-time">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{i18n.t("app.public.venue.time")}</span>
                  </div>
                  <span className="font-medium">
                    {DateTime.fromISO(startTime).toFormat("HH:mm")} - {DateTime.fromISO(endTime).toFormat("HH:mm")}
                  </span>
                </div>
              )}

              {!!peopleAmount && (
                <div className="flex items-center justify-between" data-testid="public-venue-details-card-people">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>{i18n.t("app.public.venue.people_amount")}</span>
                  </div>
                  <span className="font-medium">{i18n.t("app.public.venue.guests", { count: peopleAmount })}</span>
                </div>
              )}

              {!!tableName && (
                <div className="flex items-center justify-between" data-testid="public-venue-details-card-table">
                  <div className="flex items-center gap-2 text-gray-600">
                    <span>{i18n.t("app.public.venue.table")}</span>
                  </div>
                  <span className="font-medium">
                    {tableName}
                    {!!tableGroupName && ` (${tableGroupName})`}
                  </span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default VenueDetailsCard
